// =============================================================================
// chat-client.ts — Client API du CHAT 1-to-1 temps réel (R15-COM-02), DOUBLABLE.
// =============================================================================
// Miroir HTTP des endpoints du BC communication (chat/thread). DEUX PROFILS, DEUX PRÉFIXES,
// un SEUL contrat (les routes sont identiques sur les deux bases) :
//   - ADMIN      → base `/api/communication/admin` (AdminSessionGuard ; mutations exigent `x-csrf-token`
//                  = cookie non-httpOnly `admin_csrf`) ;
//   - FOURNISSEUR → base `/api/communication`       (TenantInterceptor ; mutations exigent `x-csrf-token`
//                  = cookie non-httpOnly `supplier_csrf`).
//
// DEC-31 §1 : le MONTANT est 100% REST. Le socket (COM-01) ne transporte QUE des signaux MINCES
// (`{ threadId, messageId }`) et ne fait QUE déclencher un refetch REST — AUCUNE donnée métier n'est
// dérivée du payload socket. Le CONTENU (texte des messages, aperçus) vient TOUJOURS de ces réponses
// REST re-cloisonnées par le RLS à l'appelant autorisé (PII, jamais sur le bus, jamais sur le socket).
//
// CONTENU = TEXTE BRUT : rendu JSX échappé côté écran (`{content}`), JAMAIS `dangerouslySetInnerHTML`.
// SÉCURITÉ : le secret de session (cookie httpOnly) n'est JAMAIS lu ici — porté par `credentials:'include'`.
// Le siret/contactId/subject viennent de la SESSION côté API (jamais du body). ZÉRO donnée métier en
// localStorage (RULE_REF_006). Horodatages = ISO 8601 (string).

/** Profil d'appel : gouverne le préfixe de base ET le cookie CSRF double-submit. */
export type ChatProfile = 'admin' | 'supplier';

/** Valeurs de `message.sender_type` (parité base — API `chat-caller.ts`). */
export type ChatSenderType = 'Admin' | 'SupplierContact';

/** Valeurs de `message.sender_role_at_send` (parité base — API `chat-caller.ts`). */
export type ChatSenderRole = 'Admin' | 'Master' | 'Collaborateur';

/** État d'un fil (parité base — API `chat.dto.ts`). */
export type ChatThreadStateValue = 'Provisionne' | 'Actif' | 'Archive';

/** Une ligne de la liste des fils (panneau gauche Admin / liste Fournisseur). Parité DTO API. */
export interface ThreadSummaryDto {
  readonly threadId: string;
  readonly campaignId: string;
  /** SIRET du fournisseur du fil (affichage admin ; le fournisseur ne voit que le sien). */
  readonly supplierSiret: string;
  /**
   * P-6 — RAISON SOCIALE du fournisseur (admin seulement). OPTIONNELLE : absente quand le SIRET est
   * inconnu du référentiel, auquel cas l'écran retombe sur le SIRET. Une conversation se tient avec une
   * entreprise, pas avec une immatriculation.
   */
  readonly supplierRaisonSociale?: string;
  readonly state: ChatThreadStateValue;
  /** ISO 8601 — dernière activité (tri récence). */
  readonly lastActivityAt: string;
  /** Nombre de messages de la CONTREPARTIE non encore lus par l'appelant (badge non-lus). */
  readonly unreadCount: number;
  /** Aperçu (tronqué) du dernier message — PII, REST-only. `null` si le fil n'a aucun message. */
  readonly lastMessagePreview: string | null;
  /** ISO 8601 — horodatage du dernier message. `null` si aucun message. */
  readonly lastMessageAt: string | null;
}

/** Réponse de la liste des fils. */
export interface ThreadsListDto {
  readonly threads: readonly ThreadSummaryDto[];
  /** Somme des non-lus (badge global de navigation). */
  readonly totalUnread: number;
}

/** Un message d'un fil (vue chronologique). `content` = texte brut, rendu JSX ÉCHAPPÉ côté front. */
export interface MessageDto {
  readonly messageId: string;
  readonly senderType: ChatSenderType;
  /** Ref opaque de l'auteur (contactId ou subject admin) — clé React, non affichée telle quelle. */
  readonly senderRef: string;
  readonly senderRoleAtSend: ChatSenderRole;
  /** Contenu texte brut (PII, REST-only). */
  readonly content: string;
  /** ISO 8601. */
  readonly sentAt: string;
  /** `true` si la contrepartie a lu ce message (indicateur « lu » côté expéditeur). */
  readonly readByCounterpart: boolean;
  /** ISO 8601 de la 1re lecture par la contrepartie, ou `null`. */
  readonly firstReadAt: string | null;
  /** `true` si ce message a été émis par l'appelant (rendu aligné « moi »). */
  readonly mine: boolean;
}

/** Réponse de la vue chronologique d'un fil. */
export interface ThreadMessagesDto {
  readonly threadId: string;
  readonly campaignId: string;
  readonly supplierSiret: string;
  /**
   * P-6 — RAISON SOCIALE du fournisseur (admin seulement). OPTIONNELLE : absente quand le SIRET est
   * inconnu du référentiel, auquel cas l'écran retombe sur le SIRET. Une conversation se tient avec une
   * entreprise, pas avec une immatriculation.
   */
  readonly supplierRaisonSociale?: string;
  readonly state: ChatThreadStateValue;
  readonly messages: readonly MessageDto[];
}

/** Résultat d'un envoi de message. */
export interface SendMessageResultDto {
  readonly messageId: string;
  readonly sentAt: string;
}

/** Résultat d'un marquage lu (nb de messages de la contrepartie nouvellement marqués lus). */
export interface MarkReadResultDto {
  readonly readCount: number;
}

/**
 * Filtres de la liste des fils (état de VUE porté par l'URL, RULE_REF_006). `q` (recherche SIRET) n'a
 * de sens QUE pour l'admin (le fournisseur ne voit que le sien) : ignoré côté supplier.
 */
export interface ThreadFilters {
  readonly campaignId?: string;
  readonly unreadOnly?: boolean;
  /** ADMIN uniquement : recherche par SIRET. */
  readonly q?: string;
}

/** Genre d'erreur applicative du chat, mappé pour un message UX explicite (parité supplier-history). */
export type ChatApiErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'generic';

/**
 * Erreur applicative renvoyée par l'API chat (HTTP 4xx/5xx). L'UI l'affiche telle quelle ; elle ne
 * réinterprète JAMAIS la règle métier (autorité = API). Le `kind` permet un libellé UX ciblé.
 */
export class ChatApiError extends Error {
  public readonly kind: ChatApiErrorKind;

  public constructor(message: string, kind: ChatApiErrorKind = 'generic') {
    super(message);
    this.name = 'ChatApiError';
    this.kind = kind;
  }
}

/**
 * Contrat du client CHAT — miroir des endpoints du BC communication (mêmes routes sur les deux bases).
 * Le montant est 100% REST (DEC-31 §1). Une INSTANCE est liée à UN profil (admin / supplier) : le préfixe
 * de base et le cookie CSRF sont figés à la construction, jamais choisis par l'appelant.
 */
export interface ChatClient {
  /** GET /threads — liste des fils (filtrable par campagne / non-lus ; +ADMIN : recherche SIRET `q`). */
  listThreads(filters: ThreadFilters): Promise<ThreadsListDto>;
  /** GET /threads/:threadId/messages — vue chronologique d'un fil (contenu REST-only, RLS). */
  getMessages(threadId: string): Promise<ThreadMessagesDto>;
  /** POST /threads/:threadId/messages — envoi d'un message (corps `{ content }` UNIQUEMENT). */
  sendMessage(threadId: string, content: string): Promise<SendMessageResultDto>;
  /** POST /threads/:threadId/read — marque lus les messages de la contrepartie du fil. */
  markRead(threadId: string): Promise<MarkReadResultDto>;
}
